import {
	Component,
	Input,
	Output,
	EventEmitter,
	ChangeDetectionStrategy
} from '@angular/core';
import {MatTooltip} from '@angular/material/tooltip';
import {ColumnConfig} from '../../models/data-table.models';
import {getComponentByName} from '../../../../../utils/custom-datatable-config';

/**
 * Renders a single cell in the data table.
 * Uses OnPush change detection for performance.
 */
@Component({
	selector: 'data-table-cell',
	templateUrl: './data-table-cell.component.html',
	styleUrls: ['./data-table-cell.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class DataTableCellComponent
{
	@Input() column!: ColumnConfig;
	@Input() row: any;
	@Input() rowIndex!: number;
	@Input() isEditing = false;
	@Input() isNewRow = false;
	@Input() columnWidth = 100;
	@Input() disabled = false;
	@Input() errors: any;
	@Input() filterValues: Record<string, any[]> = {};
	@Input() datasetName: string = '';
	@Input() tableName: string = '';


	@Output() valueChange = new EventEmitter<any>();
	@Output() cellClick = new EventEmitter<{ action: string; rowIndex: number }>();
	@Output() errorChange = new EventEmitter<{ error: boolean; rowIndex: number; columnName: string }>();
	@Output() radioClick = new EventEmitter<{ column: ColumnConfig; rowIndex: number }>();

	// Cache for custom component
	private _customComponentCache: Map<string, any> = new Map();

	get value(): any
	{
		return this.row?.[this.column.columnName];
	}

	get isEditMode(): boolean
	{
		return this.isEditing || this.isNewRow;
	}

	get cellWidth(): number
	{
		return this.column.type === 'DTY_DATE' ? 215 : this.columnWidth;
	}

	get hasError(): boolean
	{
		return !!this.errors?.[this.column.columnName];
	}

	get errorInfo(): any
	{
		return this.errors?.[this.column.columnName];
	}

	/**
	 * Gets the display value for list type columns
	 */
	getListDisplayValue(): string
	{
		const options = this.filterValues[this.column.columnName];
		if (!options) return this.value ?? '';

		const option = options.find(opt => opt.id == this.value);
		return option?.name ?? this.value ?? '';
	}

	/**
	 * Gets custom component (cached)
	 */
	getCustomComponent(): any
	{
		const columnId = this.column.id;
		if (!this._customComponentCache.has(columnId))
		{
			this._customComponentCache.set(columnId, getComponentByName(columnId));
		}
		return this._customComponentCache.get(columnId);
	}

	/**
	 * Checks if the value represents a truthy boolean
	 */
	isTruthyValue(val: any): boolean
	{
		return !!val && (val === 'Y' || val === true || val === 'true');
	}

	onValueChange(newValue: any): void
	{
		this.row[this.column.columnName] = newValue;
		this.valueChange.emit({
			rowIndex: this.rowIndex,
			columnName: this.column.columnName,
			value: newValue
		});
	}

	onCellClick(action: string): void
	{
		this.cellClick.emit({action, rowIndex: this.rowIndex});
	}

	onErrorChange(hasError: boolean): void
	{
		this.errorChange.emit({
			error: hasError,
			rowIndex: this.rowIndex,
			columnName: this.column.columnName
		});
	}

	onRadioClick(): void
	{
		this.radioClick.emit({column: this.column, rowIndex: this.rowIndex});
	}

	showTooltipIfTruncated(event: MouseEvent, tooltip: MatTooltip): void
	{
		const el = event.currentTarget as HTMLElement;
		if (el.scrollWidth > el.clientWidth)
		{
			tooltip.disabled = false;
			tooltip.show();
		}
		else
		{
			tooltip.disabled = true;
		}
	}

	//[DM - 04/07/2025 - ITOEM-64] OEMCC Monitor - highlight missing data for lines in warning status based on a Regex
	// Added method for detecting errors in cells based on given regex
	/**
	 * Validates a cell value against all regex patterns and returns comprehensive validation results with tooltips.
	 *
	 * This method checks if a cell value matches any of the regex validation rules defined
	 * for a specific column. It processes all regex patterns completely and collects all
	 * validation errors, providing a comprehensive list of issues found in the cell value.
	 *
	 * @param cellValue - The value to validate. Can be of any type but will be converted to string for validation.
	 * @param column - The column configuration object containing columnName and other metadata.
	 * @param dataset - The dataset configuration object containing column definitions and validation rules.
	 *
	 * @returns {Object} Validation result object containing:
	 *   - class: String indicating CSS class to apply ('warning' if any validation failures found, empty string for success)
	 *   - tooltips: Array of all error messages from failed validations (empty array if no errors)
	 *
	 * @remarks
	 * - The method processes ALL regex patterns and collects ALL validation errors
	 * - Does not stop at the first validation failure, ensuring complete error reporting
	 * - Invalid regex patterns are logged to console but don't stop processing of other rules
	 * - Null/undefined cell values are converted to empty strings for validation
	 * - The method assumes that regex patterns matching the input indicate validation failures
	 * - Requires the helper method `createRegexFromString()` to be available in the same class
	 * - Returns warning class only if at least one validation error is found
	 */
	getRegexpErrorsWithTooltips(cellValue: any, column: any, tableName: any)
	{
		// Empty response
		const emptyResponse = {
			class: '',
			tooltips: []
		}

		// Regex validation is available only for oemcc_monitor_view table and if input data is valid
		if (tableName !== 'oemcc_monitor_view' || !column || !column.columnName){
			return emptyResponse;
		}


		// Find the specific column configuration within the dataset
		//const columnData = dataset.datas?.find(
		//	(item) => item.columnName === column.columnName
		//);


		// Extract the list of regex validation rules for this column
		const regExpList = column?.regExpList;

		// Early return if no validation rules exist for this column
		if (!regExpList?.length) {
			return emptyResponse;
		}

		// Convert cellValue to string for regexp validation
		const stringValue = cellValue != null ? String(cellValue) : '';

		// Array to store tooltips if necessary
		const tooltips = [];

		// Iterate through all regex rules defined for this column
		for (const regExpItem of regExpList) {
			try {
				// Create a RegExp object from the string pattern using helper method
				// This handles both simple patterns and complex regex with flags
				const regex = this.createRegexFromString(regExpItem.regExp);

				// Test the string value against the current regex pattern
				// If the regex matches, it indicates a validation failure (warning condition)
				// Note: This logic assumes that matching patterns represent invalid data
				if (regex.test(stringValue)) {
					tooltips.push(regExpItem.regExpErrorMessage)
				}
			} catch (error) {
				// Log invalid regex patterns for debugging purposes
				console.warn(`Invalid regex pattern: ${regExpItem.regExp}`, error);

				// Continue processing other rules even if one regex compilation fails
				// This ensures that one invalid pattern doesn't break the entire validation
			}
		}

		// Return warning class and all collected tooltips if any errors found
		if (tooltips.length > 0) {
			return {
				class: 'warning',
				tooltips: tooltips
			};
		}

		// Return empty string if no regex patterns matched (validation passed)
		return emptyResponse;
	}

	//[DM - 04/07/2025 - ITOEM-64] OEMCC Monitor - highlight missing data for lines in warning status based on a Regex
	// Added method to convert strings to RegExps objects
	/**
	 * Creates a RegExp object from a string pattern, handling both simple and complex formats.
	 *
	 * This helper method parses regex strings that may be in different formats:
	 * - Full regex format: "/pattern/flags" (e.g., "/^test$/gi")
	 * - Simple pattern format: "pattern" (e.g., "^test$")
	 *
	 * @param regexpString - The regex pattern as a string
	 * @returns {RegExp} A compiled RegExp object ready for testing
	 *
	 * @throws {Error} Throws if the regex pattern is invalid or cannot be compiled
	 */
	private createRegexFromString(regexpString: string): RegExp {
		// Parse regex strings in the format "/pattern/flags"
		// This regex pattern extracts the main pattern and optional flags
		// Pattern breakdown: ^\/(.+)\/([gimuy]*)$
		// - ^ and $ ensure full string match
		// - \/ matches literal forward slashes
		// - (.+) captures the regex pattern (non-greedy)
		// - ([gimuy]*) captures optional regex flags
		const match = regexpString.match(/^\/(.+)\/([gimuy]*)$/);

		if (match) {
			// Destructure the match results
			// The first element [0] is the full match, so we skip it with comma
			const [, pattern, flags] = match;

			// Create RegExp with extracted pattern and flags
			return new RegExp(pattern, flags);
		}

		// If the string doesn't match the "/pattern/flags" format,
		// treat it as a simple pattern without flags
		return new RegExp(regexpString);
	}


}
